import type { WidgetType } from '../types';
import { StatusBoardWidget } from './StatusBoardWidget';
import { BudgetGaugeWidget } from './BudgetGaugeWidget';
import { EventLogWidget } from './EventLogWidget';
import { FindingsStreamWidget } from './FindingsStreamWidget';
import { GoalFrameWidget } from './GoalFrameWidget';
import { LineageTreeWidget } from './LineageTreeWidget';
import { WakeMonitorWidget } from './WakeMonitorWidget';
import { CostTrackerWidget } from './CostTrackerWidget';
import { DraftViewerWidget } from './DraftViewerWidget';
import { AnnotationInputWidget } from './AnnotationInputWidget';
import { SqlEditorWidget } from './SqlEditorWidget';
import { QueryResultsWidget } from './QueryResultsWidget';
import { ArtifactTimelineWidget } from './ArtifactTimelineWidget';
import { PromptEditorWidget } from './PromptEditorWidget';
import { McpServerBrowserWidget } from './McpServerBrowserWidget';
import { DataSourceExplorerWidget } from './DataSourceExplorerWidget';
import { SkillsPanelWidget } from './SkillsPanelWidget';
import { EnvSwitcherWidget } from './EnvSwitcherWidget';
import { CopilotPanelWidget } from './CopilotPanelWidget';
import { ActiveContextWidget } from './ActiveContextWidget';

export interface WidgetProps {
  journalRootId?: string;
  widgetProps?: Record<string, unknown>;
}

const WIDGET_MAP: Record<WidgetType, React.ComponentType<WidgetProps>> = {
  'status-board': StatusBoardWidget,
  'budget-gauge': BudgetGaugeWidget,
  'event-log': EventLogWidget,
  'findings-stream': FindingsStreamWidget,
  'goal-frame': GoalFrameWidget,
  'lineage-tree': LineageTreeWidget,
  'wake-monitor': WakeMonitorWidget,
  'cost-tracker': CostTrackerWidget,
  'draft-viewer': DraftViewerWidget,
  'annotation-input': AnnotationInputWidget,
  'sql-editor': SqlEditorWidget,
  'query-results': QueryResultsWidget,
  'artifact-timeline': ArtifactTimelineWidget,
  'prompt-editor': PromptEditorWidget,
  'mcp-server-browser': McpServerBrowserWidget,
  'data-source-explorer': DataSourceExplorerWidget,
  'skills-panel': SkillsPanelWidget,
  'env-switcher': EnvSwitcherWidget,
  'copilot-panel': CopilotPanelWidget,
  'active-context': ActiveContextWidget,
};

export function getWidgetComponent(type: WidgetType): React.ComponentType<WidgetProps> | null {
  return WIDGET_MAP[type] || null;
}

export const WIDGET_CATALOG: { type: WidgetType; label: string; description: string; defaultW: number; defaultH: number }[] = [
  { type: 'status-board', label: 'Status Board', description: 'Active, sleeping, waiting continuation counts', defaultW: 3, defaultH: 3 },
  { type: 'budget-gauge', label: 'Budget Gauge', description: 'Budget spend vs caps with visual gauges', defaultW: 3, defaultH: 3 },
  { type: 'event-log', label: 'Event Log', description: 'Real-time event stream from all continuations', defaultW: 4, defaultH: 4 },
  { type: 'findings-stream', label: 'Findings Stream', description: 'Published findings with confidence and sources', defaultW: 5, defaultH: 5 },
  { type: 'goal-frame', label: 'Goal Frame', description: 'Current goal frame with edit controls', defaultW: 4, defaultH: 3 },
  { type: 'lineage-tree', label: 'Lineage Tree', description: 'Visual tree of continuation lineage', defaultW: 6, defaultH: 6 },
  { type: 'wake-monitor', label: 'Wake Monitor', description: 'What each sleeping continuation is waiting for', defaultW: 3, defaultH: 3 },
  { type: 'cost-tracker', label: 'Cost Tracker', description: 'Cost by model tier and tool usage', defaultW: 3, defaultH: 3 },
  { type: 'draft-viewer', label: 'Draft Viewer', description: 'Current working draft of the research output', defaultW: 5, defaultH: 4 },
  { type: 'annotation-input', label: 'Annotation Input', description: 'Add researcher annotations and signals', defaultW: 3, defaultH: 3 },
  { type: 'sql-editor', label: 'SQL Editor', description: 'Edit, test, and version SQL queries for MCP tools', defaultW: 6, defaultH: 5 },
  { type: 'query-results', label: 'Query Results', description: 'Sortable data table for SQL test output', defaultW: 6, defaultH: 4 },
  { type: 'artifact-timeline', label: 'Artifact Timeline', description: 'Version history with promote/rollback controls', defaultW: 4, defaultH: 5 },
  { type: 'prompt-editor', label: 'Prompt Editor', description: 'Edit agent system prompts with variable preview', defaultW: 6, defaultH: 5 },
  { type: 'mcp-server-browser', label: 'MCP Browser', description: 'Browse MCP data sources and their SQL tools', defaultW: 4, defaultH: 5 },
  { type: 'data-source-explorer', label: 'Schema Explorer', description: 'Browse schemas, tables, and columns from artifacts', defaultW: 3, defaultH: 5 },
  { type: 'skills-panel', label: 'BarnSkills', description: 'Relevant skill playbooks for the active artifact type', defaultW: 3, defaultH: 4 },
  { type: 'env-switcher', label: 'Env Switcher', description: 'Select which database environment each product queries (per journal)', defaultW: 3, defaultH: 4 },
  { type: 'copilot-panel', label: 'Studio Copilot', description: 'Agent that sees your editor — explains SQL and proposes draft edits', defaultW: 3, defaultH: 9 },
  { type: 'active-context', label: 'Active Context', description: 'The customer/site/diet scope the agent answers about — switchable, flagged', defaultW: 3, defaultH: 4 },
];
